import jsPDF from "jspdf";

const MARGIN = 10;
const TOP_MARGIN = 12;
const BOTTOM_MARGIN = 12;
const GROUP_GAP = 2;
const GROUP_HEIGHT = 8;
const MIN_ROW_HEIGHT = 18;
const LABEL_STEP = 3.8;
const VALUE_STEP = 4.6;
const CELL_BASE_HEIGHT = 14;

const wrap = (doc, value, width) => {
  const lines = doc.splitTextToSize(String(value ?? ""), Math.max(3, width));
  return Array.isArray(lines) && lines.length ? lines : [""];
};

const printableValue = (input) => {
  let value = input;
  if (value && typeof value === "object") {
    if (Array.isArray(value.selected) && Array.isArray(value.options)) {
      const labelsById = new Map();
      value.options.forEach((option) => {
        if (!option) return;
        if (typeof option === "string") {
          labelsById.set(option, option);
          return;
        }
        const key = option.id ?? option.value ?? option.label ?? option.name;
        if (key == null) return;
        labelsById.set(String(key), String(option.label ?? option.name ?? option.value ?? key));
      });
      const selected = value.selected
        .map((key) => labelsById.get(String(key)) || String(key))
        .filter((label) => label.trim().length);
      value = selected.length
        ? selected.join(", ")
        : labelsById.size ? "Opções: " + [...labelsById.values()].join(", ") : "";
    } else if (value.name) {
      value = value.name;
    } else {
      value = JSON.stringify(value);
    }
  }
  return String(value ?? "");
};

const fieldSpan = (field) => {
  const requested = Number(field?.size || 12);
  return Number.isFinite(requested) ? Math.min(12, Math.max(1, Math.floor(requested))) : 12;
};

const cellHeight = (labelCount, valueCount) =>
  Math.max(MIN_ROW_HEIGHT, CELL_BASE_HEIGHT +
    Math.max(0, labelCount - 1) * LABEL_STEP +
    Math.max(0, valueCount - 1) * VALUE_STEP);

const prepareRows = (doc, fields, values, columnWidth) => {
  const rows = [];
  let row = [];
  let used = 0;
  for (const field of fields) {
    const span = fieldSpan(field);
    if (row.length && used + span > 12) {
      rows.push(row);
      row = [];
      used = 0;
    }
    const width = span * columnWidth;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    const labels = wrap(doc, field.label || field.id || "", width - 4);
    doc.setFontSize(11);
    const lines = wrap(doc, printableValue(values[field.id]), width - 4);
    row.push({ span, width, labels, lines });
    used += span;
  }
  if (row.length) rows.push(row);
  return rows;
};

/**
 * Renderiza o formulário completo, sem altura fixa de célula e com quebras de
 * página antes de perder qualquer linha. Linhas excepcionalmente longas podem
 * continuar na página seguinte; grupos recebem título de continuação.
 */
export const renderRequestPdf = (doc, { instance, form, values = {} }) => {
  if (!instance) return doc;
  doc.setFont("helvetica", "normal");
  doc.setLineWidth(0.1);
  doc.setLineHeightFactor(1.15);

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const contentWidth = pageWidth - MARGIN * 2;
  const columnWidth = contentWidth / 12;
  const pageBottom = pageHeight - BOTTOM_MARGIN;
  let y = MARGIN;
  let activeGroup = "";

  const groupTitle = (label, continuation = false) => {
    const labelText = continuation ? label + " (continuação)" : label;
    doc.setFontSize(11);
    const lines = wrap(doc, labelText, contentWidth - 4);
    const height = Math.max(GROUP_HEIGHT, 3 + lines.length * 4.8);
    doc.rect(MARGIN, y, contentWidth, height);
    doc.text(lines, MARGIN + contentWidth / 2, y + 5, { align: "center" });
    y += height;
    return height;
  };

  const nextPage = () => {
    doc.addPage();
    y = TOP_MARGIN;
    if (activeGroup) groupTitle(activeGroup, true);
  };

  const drawRow = (cells) => {
    const fullHeight = Math.max(...cells.map((cell) =>
      cellHeight(cell.labels.length, cell.lines.length)));
    const freshHeight = pageBottom - TOP_MARGIN -
      (activeGroup ? Math.max(GROUP_HEIGHT, 3 +
        wrap(doc, activeGroup + " (continuação)", contentWidth - 4).length * 4.8) : 0);

    // Preserva as linhas inteiras quando cabem em uma página vazia.
    if (fullHeight <= freshHeight && y + fullHeight > pageBottom) nextPage();

    const cursor = cells.map(() => 0);
    while (true) {
      if (pageBottom - y < MIN_ROW_HEIGHT) nextPage();

      const available = pageBottom - y;
      const chunks = cells.map((cell, index) => {
        const base = CELL_BASE_HEIGHT + Math.max(0, cell.labels.length - 1) * LABEL_STEP;
        const maxLines = Math.max(1, Math.floor((available - base) / VALUE_STEP) + 1);
        const portion = cell.lines.slice(cursor[index], cursor[index] + maxLines);
        return portion;
      });
      const height = Math.max(MIN_ROW_HEIGHT, ...cells.map((cell, i) =>
        cellHeight(cell.labels.length, chunks[i].length)));

      if (height > available + 0.01) {
        // Uma legenda longa pode ocupar mais espaço que o restante da página.
        // A quebra acontece antes de desenhar qualquer conteúdo fora da folha.
        nextPage();
        continue;
      }

      let x = MARGIN;
      cells.forEach((cell, i) => {
        doc.rect(x, y, cell.width, height);
        doc.setFontSize(9);
        doc.text(cell.labels, x + 2, y + 5);
        if (chunks[i].length) {
          doc.setFontSize(11);
          doc.text(chunks[i], x + 2,
            y + 11 + Math.max(0, cell.labels.length - 1) * LABEL_STEP);
        }
        cursor[i] += chunks[i].length;
        x += cell.width;
      });
      y += height;

      if (cells.every((cell, i) => cursor[i] >= cell.lines.length)) return;
      nextPage();
    }
  };

  const processName = String(instance.process?.name || "Processo");
  const headerTitle = "Solicitação " + instance.id + " - " + processName;
  doc.setFontSize(14);
  const titleLines = wrap(doc, headerTitle, contentWidth);
  doc.text(titleLines, MARGIN, y);
  y += titleLines.length * 6 + 2;

  const requester =
    instance.requester?.name || instance.user?.name || instance.startedBy?.name || "-";
  const startedAt = instance.startedAt ? new Date(instance.startedAt) : null;
  const startedText = startedAt && !Number.isNaN(startedAt.getTime())
    ? startedAt.toLocaleString("pt-BR") : "-";
  for (const header of [
    "ID: " + instance.id,
    "Processo: " + processName,
    "Solicitante: " + requester,
    "Início: " + startedText,
  ]) {
    doc.setFontSize(10);
    const lines = wrap(doc, header, contentWidth);
    if (y + lines.length * 5 > pageBottom) {
      doc.addPage();
      y = TOP_MARGIN;
    }
    doc.text(lines, MARGIN, y);
    y += lines.length * 5;
  }
  y += 3;

  const groups = Array.isArray(form?.schema?.groups) ? form.schema.groups : [];
  const fields = Array.isArray(form?.schema?.fields) ? form.schema.fields : [];
  const orderedGroups = [...groups, {
    id: "__ungrouped__",
    label: groups.length ? "Campos" : "",
  }];

  for (const group of orderedGroups) {
    const selected = fields.filter((field) =>
      (field.groupId || "__ungrouped__") === (group.id || "__ungrouped__") &&
      Object.prototype.hasOwnProperty.call(values, field.id));
    if (!selected.length) continue;
    const rows = prepareRows(doc, selected, values, columnWidth);
    activeGroup = String(group.label || "");

    if (activeGroup) {
      doc.setFontSize(11);
      const headingLines = wrap(doc, activeGroup, contentWidth - 4);
      const headingHeight = Math.max(GROUP_HEIGHT, 3 + headingLines.length * 4.8);
      const firstRowHeight = Math.max(...rows[0].map((cell) =>
        cellHeight(cell.labels.length, cell.lines.length)));
      const maxRowHeightOnFreshPage = pageBottom - TOP_MARGIN - headingHeight;
      const firstRowRequired = firstRowHeight > maxRowHeightOnFreshPage
        ? MIN_ROW_HEIGHT : firstRowHeight;
      // Não deixa o título do grupo sozinho no rodapé sem seus campos.
      if (y + headingHeight + firstRowRequired > pageBottom) {
        doc.addPage();
        y = TOP_MARGIN;
      }
      groupTitle(activeGroup);
    }
    rows.forEach(drawRow);
    y += GROUP_GAP;
  }

  activeGroup = "";
  const pages = doc.getNumberOfPages();
  for (let page = 1; page <= pages; page += 1) {
    doc.setPage(page);
    doc.setFontSize(8);
    doc.text("Página " + page + " de " + pages,
      pageWidth - MARGIN, pageHeight - 6, { align: "right" });
  }
  return doc;
};

export const createRequestPdf = (options) =>
  renderRequestPdf(new jsPDF({ compress: true }), options);
